import React from 'react'
import styled from '@emotion/styled'
import { useQuery } from '@tanstack/react-query'
import { useSearchParams } from 'react-router'
import { useDebouncedValue } from 'rooks'
import { measurements, spacing } from '@mobi/component-library/Foundations'
import { TabButton, Tabs } from '@mobi/component-library/Navigation/Tabs'
import { TabGroupStyled } from '@mobi/component-library/Navigation/Tabs/Components/Common.styles'
import { hexColors } from '@mobi/settings/hexColors'
import { useSearchParamState } from '@mobi/utils/hooks/useSearchParamState'
import { useDisableAppHeaderSticky } from '@core/Areas/AppHeader/hooks/useDisableAppHeaderSticky'
import { useNewSportsOfferingFeature } from '@core/Areas/KambiSports/Offering/hooks/useNewSportsOfferingFeature'
import { MainContainer } from '@core/Components/Containers'
import { SearchField } from './Components/Common/SearchField'
import { RacingSearch } from './Components/RacingSearch'
import { SportsSearch } from './Components/SportsSearch'
import { trackSearchInteraction } from './helpers/analytics'
import { racingSearchQuery } from './helpers/api/racingSearchQuery'
import { sportsSearchQuery } from './helpers/api/sportsSearchQuery'
import type { SearchModeType } from './types'

export const SearchPage: React.FC = () => {
  useDisableAppHeaderSticky()
  const isNewSportsFeatureActive = useNewSportsOfferingFeature()

  const [currentSearchMode, setCurrentSearchMode] = useSearchParamState<SearchModeType>({
    key: 'tab',
    values: ['racing', 'sports'],
    defaultValue: 'racing',
    shouldSetValueOnMount: true,
  })

  React.useEffect(() => {
    trackSearchInteraction({ interaction_type: 'Search Opened' })
  }, [])

  const [searchParams, setSearchParams] = useSearchParams()
  const initialSearchTerm = searchParams.get('search') || ''

  const isRacingSearch = currentSearchMode === 'racing'
  const isSportsSearch = currentSearchMode === 'sports'

  const [searchWasImmediate, setSearchWasImmediate] = React.useState(false)

  const [racingSearchTerm, setRacingSearchTerm] = React.useState<string>(
    setInitialSearchTerm(isRacingSearch, initialSearchTerm)
  )
  const [racingSearchTermQuery, setRacingSearchTermQuery] = useDebouncedValue(racingSearchTerm, 500)

  const [sportsSearchTerm, setSportsSearchTerm] = React.useState<string>(
    setInitialSearchTerm(isSportsSearch, initialSearchTerm)
  )
  const [sportsSearchTermQuery, setSportsSearchTermQuery] = useDebouncedValue(sportsSearchTerm, 500)

  const racingQuery = useQuery(
    racingSearchQuery({ searchTerm: racingSearchTermQuery, isEnabled: isRacingSearch })
  )
  const sportsQuery = useQuery(
    sportsSearchQuery({ searchTerm: sportsSearchTermQuery, isEnabled: isSportsSearch })
  )

  const updateSearchTerm = (searchTerm: string, isImmediate?: boolean) => {
    setSearchWasImmediate(isImmediate || false)
    if (isRacingSearch) {
      setRacingSearchTerm(searchTerm)
      isImmediate && setRacingSearchTermQuery(searchTerm)
    } else {
      setSportsSearchTerm(searchTerm)
      isImmediate && setSportsSearchTermQuery(searchTerm)
    }
    if (searchTerm.length > 3) {
      setSearchParams(
        prevParams => {
          const newParams = new URLSearchParams(prevParams)
          newParams.set('search', searchTerm)
          return newParams
        },
        { replace: true }
      )
    }
  }

  const handleClearSearchResults = () => updateSearchTerm('', true)
  const handleInputValueChange = (searchTerm: string) => updateSearchTerm(searchTerm)
  const handleImmediateValueChange = (searchTerm: string) => updateSearchTerm(searchTerm, true)

  const activeSearchTerm = isRacingSearch ? racingSearchTerm : sportsSearchTerm

  const isRacingLoading =
    racingQuery.isLoading ||
    (racingSearchTerm.length > 2 && racingSearchTerm !== racingSearchTermQuery)

  const isSportsLoading =
    sportsQuery.isLoading ||
    (racingSearchTerm.length > 2 && sportsSearchTerm !== sportsSearchTermQuery)

  return (
    <MainContainer background='surface300'>
      <WrapperStyled data-testid='Search'>
        <header>
          <SearchField
            value={activeSearchTerm}
            searchMode={currentSearchMode}
            onValueChange={handleInputValueChange}
            onClearResults={handleClearSearchResults}
          />
        </header>

        {isNewSportsFeatureActive && (
          <TabGroupStyled>
            <Tabs bottomBorder='none'>
              <TabButton
                content='Racing'
                onClick={() => setCurrentSearchMode('racing')}
                isSelected={isRacingSearch}
              />
              <TabButton
                content='Sports'
                onClick={() => setCurrentSearchMode('sports')}
                isSelected={isSportsSearch}
              />
            </Tabs>
          </TabGroupStyled>
        )}

        <section>
          {isRacingSearch ? (
            <RacingSearch
              searchTerm={racingSearchTerm}
              isError={racingQuery.isError}
              searchWasImmediate={searchWasImmediate}
              searchResults={racingQuery.data?.results}
              handleImmediateSearch={handleImmediateValueChange}
              isLoading={isRacingLoading}
            />
          ) : (
            <SportsSearch
              searchTerm={sportsSearchTerm}
              isError={sportsQuery.isError}
              searchResults={sportsQuery.data}
              searchWasImmediate={searchWasImmediate}
              handleImmediateSearch={handleImmediateValueChange}
              isLoading={isSportsLoading}
            />
          )}
        </section>
      </WrapperStyled>
    </MainContainer>
  )
}

// =============
// Local Helpers
// =============

function setInitialSearchTerm(isActive: boolean, searchTerm: string): () => string {
  return () => (isActive && searchTerm) || ''
}

// ======
// Styles
// ======

const WrapperStyled = styled.div({
  '> header': {
    position: 'sticky',
    top: 0,
    boxSizing: 'border-box',
    zIndex: 2,
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    height: measurements.mobi.headerHeight,
    minHeight: measurements.mobi.headerHeight,
    paddingInline: spacing.sm,
    background: hexColors.blueDiamond,
  },

  '> section': {
    padding: `${spacing.sm} ${spacing.md}`,
  },
})
